// LLM Service Integration
// This service provides a unified interface for agents to use LLM functionality

import { OpenRouterAPI, MODEL_MODES } from '../LLM/index.js'

/**
 * LLM Service class that provides a unified interface for agent interactions
 */
export class LLMService {
  constructor(apiKey) {
    if (!apiKey) {
      throw new Error('OpenRouter API key is required')
    }
    this.openRouterAPI = new OpenRouterAPI(apiKey)
    this.isConnected = false
    this.lastError = null
  }

  /**
   * Test connection to OpenRouter API
   * @returns {Promise<boolean>} Connection status
   */
  async testConnection() {
    try {
      this.isConnected = await this.openRouterAPI.testConnection()
      if (this.isConnected) {
        this.lastError = null
      }
      return this.isConnected
    } catch (error) {
      console.error('LLM Service connection test failed:', error)
      this.lastError = error
      this.isConnected = false
      return false
    }
  }

  /**
   * Send message to LLM
   * @param {Array} messages - Array of message objects
   * @param {Object} options - Request options
   * @param {string} options.mode - Model mode (standard, thinking, genius, clever)
   * @param {string} options.searchType - Search type (web, academic)
   * @param {boolean} options.stream - Enable streaming
   * @param {boolean} options.consumeCredits - Whether to consume credits (for UI purposes)
   * @param {Object} options.additionalParams - Additional parameters
   * @returns {Promise} API response
   */
  async sendMessage(messages, options = {}) {
    const {
      mode = MODEL_MODES.STANDARD,
      searchType = 'web',
      stream = false,
      consumeCredits = true,
      additionalParams = {}
    } = options

    try {
      console.log('🚀 LLM Service - Sending message:', {
        mode,
        searchType,
        messageCount: messages.length,
        stream,
        consumeCredits
      })

      const response = await this.openRouterAPI.createChatCompletion({
        mode,
        messages,
        stream,
        searchType,
        additionalParams
      })

      if (stream) {
        return response // Return response object for streaming
      }

      // Extract content from non-streaming response
      const content = response?.choices?.[0]?.message?.content || ''
      console.log('✅ LLM Service - Response received:', {
        contentLength: content.length,
        mode,
        searchType
      })

      this.lastError = null
      return content

    } catch (error) {
      console.error('❌ LLM Service - Send message failed:', error)
      this.lastError = error
      throw error
    }
  }

  /**
   * Send streaming message to LLM
   * @param {Array} messages - Array of message objects
   * @param {Function} onChunk - Callback for each chunk
   * @param {Function} onComplete - Callback when complete
   * @param {Function} onError - Callback for errors
   * @param {Object} options - Request options
   */
  async sendStreamingMessage(messages, onChunk, onComplete, onError, options = {}) {
    const {
      mode = MODEL_MODES.STANDARD,
      searchType = 'web',
      additionalParams = {}
    } = options

    try {
      console.log('🌊 LLM Service - Starting streaming message:', {
        mode,
        searchType,
        messageCount: messages.length
      })

      const response = await this.openRouterAPI.createChatCompletion({
        mode,
        messages,
        stream: true,
        searchType,
        additionalParams
      })

      await this.openRouterAPI.handleStreamingResponse(
        response,
        onChunk,
        onComplete,
        onError
      )

      this.lastError = null

    } catch (error) {
      console.error('❌ LLM Service - Streaming failed:', error)
      this.lastError = error
      if (onError) {
        onError(error)
      } else {
        throw error
      }
    }
  }

  /**
   * Get current connection status
   * @returns {boolean} Connection status
   */
  getConnectionStatus() {
    return this.isConnected
  }

  /**
   * Get last error
   * @returns {Error|null} Last error or null
   */
  getLastError() {
    return this.lastError
  }

  /**
   * Change model mode
   * @param {string} mode - New model mode
   */
  setMode(mode) {
    if (Object.values(MODEL_MODES).includes(mode)) {
      this.currentMode = mode
      console.log('🔄 LLM Service - Mode changed to:', mode)
    } else {
      console.warn('⚠️ LLM Service - Invalid mode:', mode)
    }
  }

  /**
   * Get available model modes
   * @returns {Object} Available model modes
   */
  getAvailableModes() {
    return MODEL_MODES
  }
}

/**
 * Create LLM Service instance
 * @param {string} apiKey - OpenRouter API key
 * @returns {LLMService} LLM Service instance
 */
export const createLLMService = (apiKey) => {
  if (!apiKey) {
    throw new Error('OpenRouter API key is required')
  }
  return new LLMService(apiKey)
}

/**
 * Default export for convenience
 */
export default LLMService